import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { startRegistration } from '@simplewebauthn/browser';
import { theme } from '../theme';

// Action Guard admin / staff page.
//
//   • Top: policy explainer.
//   • Admin block: enable, method (PIN | Fingerprint), checkboxes for
//     which actions are guarded.
//   • PIN method shows a "Re-issue PIN" button per staff.
//   • Fingerprint method shows a "Enroll fingerprint on this device"
//     button per staff. The admin clicks while the staff member is
//     present — staff then touches the sensor on the same machine.
//
// Either method, the staff list is the same table: one row per active
// staff, with credential count + last rotation info.
export default function ActionGuardPage({ API_BASE, currentUser }) {
  const isAdmin = ['owner', 'admin', 'super_admin'].includes(currentUser?.role);

  const [cfg, setCfg] = useState({ enabled: false, method: 'pin', protectedActions: [] });
  const [actions, setActions] = useState([]);
  const [staff, setStaff] = useState([]);
  const [cfgMsg, setCfgMsg] = useState({ type: '', text: '' });
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadConfig = async () => {
    try {
      const r = await axios.get(`${API_BASE}/action-guard/config`);
      setCfg({
        enabled: !!r.data.config?.enabled,
        method:  r.data.config?.method || 'pin',
        protectedActions: r.data.config?.protectedActions || [],
      });
      setActions(r.data.actions || []);
    } catch (_) {}
    setLoading(false);
  };
  const loadStaff = async () => {
    try {
      const r = await axios.get(`${API_BASE}/action-guard/staff`);
      setStaff(r.data?.staff || []);
    } catch (_) { setStaff([]); }
  };
  useEffect(() => { loadConfig(); loadStaff(); /* eslint-disable-next-line */ }, []);

  const grouped = useMemo(() => {
    const out = {};
    for (const a of actions) (out[a.category || 'General'] ||= []).push(a);
    return out;
  }, [actions]);

  const toggleAction = (key) => {
    setCfg(c => ({
      ...c,
      protectedActions: c.protectedActions.includes(key)
        ? c.protectedActions.filter(k => k !== key)
        : [...c.protectedActions, key],
    }));
  };

  const saveCfg = async () => {
    setCfgMsg({ type: '', text: '' });
    try {
      await axios.put(`${API_BASE}/action-guard/config`, cfg);
      setCfgMsg({ type: 'ok', text: 'Config saved ✓' });
    } catch (e) {
      setCfgMsg({ type: 'err', text: e.response?.data?.error || 'Save failed' });
    }
  };

  const reissuePin = async (s) => {
    if (!window.confirm(`${s.fullName || s.username} ke liye naya PIN generate kar ke ${s.email || 'email'} pe bhejna hai? Purana PIN turant kaam karna band ho jayega.`)) return;
    setBusyId(s._id);
    try {
      const r = await axios.post(`${API_BASE}/action-guard/admin-issue`, { userId: s._id });
      alert(r.data?.message || 'New PIN sent.');
      await loadStaff();
    } catch (e) {
      alert('Fail: ' + (e.response?.data?.error || e.message));
    }
    setBusyId(null);
  };

  // Drive the WebAuthn enrollment ceremony for `targetUserId`. The current
  // tab/device is the one whose biometric sensor will be enrolled — the
  // admin clicks while the staff member is physically present.
  const enrollFingerprint = async (s) => {
    const useAdminFlow = String(s._id) !== String(currentUser?.id);
    const nickname = window.prompt(`Is fingerprint ka naam? (e.g. "${s.fullName || s.username} - reception laptop")`, `${s.fullName || s.username} - this device`);
    if (nickname === null) return;  // user hit cancel
    setBusyId(s._id);
    try {
      const optsUrl = useAdminFlow
        ? `${API_BASE}/action-guard/webauthn/admin-register/options`
        : `${API_BASE}/action-guard/webauthn/register/options`;
      const verUrl = useAdminFlow
        ? `${API_BASE}/action-guard/webauthn/admin-register/verify`
        : `${API_BASE}/action-guard/webauthn/register/verify`;
      const optsRes = await axios.post(optsUrl, useAdminFlow ? { userId: s._id } : {});
      const { options } = optsRes.data;
      const attResp = await startRegistration({ optionsJSON: options });
      const verifyBody = useAdminFlow ? { userId: s._id, response: attResp, nickname } : { response: attResp, nickname };
      await axios.post(verUrl, verifyBody);
      alert(`✅ Enrolled: ${s.fullName || s.username}`);
      await loadStaff();
    } catch (e) {
      alert('Enrollment fail: ' + (e?.response?.data?.error || e?.message || 'unknown'));
    }
    setBusyId(null);
  };

  const sectionStyle = { background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 10, padding: 20, marginBottom: 18 };
  const labelStyle = { display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#333', marginBottom: 6 };

  return (
    <div style={{ padding: 24, maxWidth: 880, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 4px', color: theme.navy }}>🔐 Action Guard</h2>
      <p style={{ margin: '0 0 20px', color: theme.textSubtle, fontSize: '0.9rem' }}>
        Sensitive actions (delete, cancel, refund) pe staff ka PIN ya fingerprint maanga jata hai —
        chahe login shared ho, audit log me asli naam record hoga.
      </p>

      <div style={{ ...sectionStyle, background: '#fff8e6', borderColor: '#fde0a8' }}>
        <strong style={{ color: '#7a4a00' }}>📌 Policy</strong>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: 22, fontSize: '0.86rem', color: '#5a3700', lineHeight: 1.7 }}>
          <li><strong>PIN method:</strong> 4-digit, system-generated, har mahine 1 tareekh ko email pe naya PIN.</li>
          <li><strong>Fingerprint method:</strong> Admin staff ka fingerprint enroll karta (Touch ID / Windows Hello / Android sensor). Same staff multiple devices pe enroll ho sakta.</li>
          <li>Ek time pe ek hi method active. Admin Settings me change kar sakta.</li>
          <li>Kuchh bhi bhul/khona ho — admin re-issue ya re-enroll kar sakta panel se.</li>
        </ul>
      </div>

      {isAdmin && (
        <>
          <div style={sectionStyle}>
            <h3 style={{ margin: '0 0 12px', fontSize: '1rem', color: theme.navy }}>Clinic config</h3>
            {loading && <div style={{ fontSize: '0.85rem', color: theme.textMuted }}>Loading…</div>}

            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={cfg.enabled} onChange={e => setCfg(c => ({ ...c, enabled: e.target.checked }))}/>
              <span style={{ fontSize: '0.92rem', color: '#111', fontWeight: 600 }}>Action Guard enable karo</span>
            </label>

            <label style={labelStyle}>Method</label>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <label style={{ flex: 1, border: `1px solid ${cfg.method === 'pin' ? '#0A7B3D' : theme.border}`, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', background: cfg.method === 'pin' ? '#f0fdf4' : '#fff' }}>
                <input type="radio" name="agmethod" checked={cfg.method === 'pin'} onChange={() => setCfg(c => ({ ...c, method: 'pin' }))} style={{ marginRight: 8 }}/>
                <strong>PIN</strong>
                <div style={{ fontSize: '0.75rem', color: theme.textSubtle, marginTop: 4 }}>4-digit, system-generated, monthly rotate.</div>
              </label>
              <label style={{ flex: 1, border: `1px solid ${cfg.method === 'fingerprint' ? '#0A7B3D' : theme.border}`, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', background: cfg.method === 'fingerprint' ? '#f0fdf4' : '#fff' }}>
                <input type="radio" name="agmethod" checked={cfg.method === 'fingerprint'} onChange={() => setCfg(c => ({ ...c, method: 'fingerprint' }))} style={{ marginRight: 8 }}/>
                <strong>Fingerprint</strong>
                <div style={{ fontSize: '0.75rem', color: theme.textSubtle, marginTop: 4 }}>Touch ID / Windows Hello / Android. Pehle staff ko enroll karwana padega.</div>
              </label>
            </div>

            <label style={labelStyle}>Kaun se actions pe verify mangna hai?</label>
            <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, padding: 12, maxHeight: 320, overflowY: 'auto', marginBottom: 12 }}>
              {Object.keys(grouped).length === 0 && (
                <div style={{ fontSize: '0.82rem', color: theme.textMuted }}>Koi action registered nahi hai abhi.</div>
              )}
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '6px 0 4px' }}>{cat}</div>
                  {items.map(a => (
                    <label key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', fontSize: '0.88rem' }}>
                      <input type="checkbox" checked={cfg.protectedActions.includes(a.key)} onChange={() => toggleAction(a.key)}/>
                      <span>{a.label}</span>
                      <span style={{ fontSize: '0.7rem', color: theme.textSubtle, marginLeft: 'auto', fontFamily: 'monospace' }}>{a.key}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>

            {cfgMsg.text && (
              <div style={{ fontSize: '0.82rem', color: cfgMsg.type === 'err' ? '#B00020' : '#0A7B3D', marginBottom: 10 }}>{cfgMsg.text}</div>
            )}
            <button onClick={saveCfg} style={{ padding: '9px 18px', background: theme.navy, color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>
              Save config
            </button>
          </div>

          <div style={sectionStyle}>
            <h3 style={{ margin: '0 0 12px', fontSize: '1rem', color: theme.navy }}>Staff status</h3>
            <p style={{ fontSize: '0.82rem', color: theme.textSubtle, marginBottom: 10 }}>
              {cfg.method === 'fingerprint'
                ? 'Click "Enroll" while the staff member is physically present at this device — they will touch the sensor.'
                : '"Re-issue PIN" daba ke kisi staff ka turant naya PIN generate kar sakte ho.'}
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
              <thead>
                <tr style={{ background: '#f6f8fa', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Staff</th>
                  <th style={{ padding: 8 }}>Role</th>
                  <th style={{ padding: 8 }}>Email</th>
                  <th style={{ padding: 8 }}>{cfg.method === 'fingerprint' ? 'Fingerprints' : 'PIN'}</th>
                  <th style={{ padding: 8 }}></th>
                </tr>
              </thead>
              <tbody>
                {staff.map(s => (
                  <tr key={s._id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={{ padding: 8 }}>{s.fullName || s.username}</td>
                    <td style={{ padding: 8, color: theme.textSubtle }}>{s.role}</td>
                    <td style={{ padding: 8, color: theme.textSubtle, fontSize: '0.78rem' }}>{s.email || <span style={{ color: '#B00020' }}>missing</span>}</td>
                    <td style={{ padding: 8 }}>
                      {cfg.method === 'fingerprint'
                        ? (s.fingerprints > 0
                            ? <span style={{ color: '#0A7B3D', fontWeight: 600 }}>● {s.fingerprints} enrolled</span>
                            : <span style={{ color: '#aa6500' }}>● not enrolled</span>)
                        : (s.hasPin
                            ? <span style={{ color: '#0A7B3D', fontWeight: 600 }}>● set ({s.pinMonth || '—'})</span>
                            : <span style={{ color: '#aa6500' }}>● not set</span>)}
                    </td>
                    <td style={{ padding: 8 }}>
                      {cfg.method === 'fingerprint' ? (
                        <button onClick={() => enrollFingerprint(s)} disabled={busyId === s._id}
                          style={{ padding: '5px 12px', background: '#0A7B3D', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: '0.78rem', cursor: busyId === s._id ? 'wait' : 'pointer' }}>
                          {busyId === s._id ? 'Working…' : 'Enroll'}
                        </button>
                      ) : (
                        <button onClick={() => reissuePin(s)} disabled={!s.email || busyId === s._id}
                          style={{ padding: '5px 12px', background: s.email ? '#0A7B3D' : '#aaa', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: '0.78rem', cursor: s.email && busyId !== s._id ? 'pointer' : 'not-allowed' }}>
                          {busyId === s._id ? 'Sending…' : 'Re-issue PIN'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {staff.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: theme.textMuted }}>No staff yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
